import { ROUTES } from './routes';
import GuestOnlyRoute from './components/common/GuestOnlyRoute';
import './index.css';
import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider } from './contexts/AuthContext';
import ScrollToTop from './components/common/ScrollToTop';
import ProtectedRoute from './components/common/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import HelpChat from './components/common/HelpChat';
import usePageTracking from './hooks/usePageTracking';

// Home is the landing page and the most common entry point, so it stays in the
// main bundle for the fastest possible first paint. Every other route is
// code-split with React.lazy so it downloads only when visited — the shop was
// previously one monolithic bundle where visiting any page pulled in all of
// them (checkout, all three long-form article pages, profile, etc.).
import Home from './pages/Home';

const Products = lazy(() => import('./pages/Products'));
const ProductDetailsPage = lazy(() => import('./pages/ProductDetailsPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const CheckoutFailedPage = lazy(() => import('./pages/CheckoutFailedPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const OrderDetailsPage = lazy(() => import('./pages/OrderDetailsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const UserFavoritesPage = lazy(() => import('./pages/UserFavoritesPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsConditionsPage = lazy(() => import('./pages/TermsConditionsPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const CategoryList = lazy(() =>
  import('./components/Categories').then((m) => ({ default: m.CategoryList }))
);
const CategoryResultsPage = lazy(() => import('./pages/CategoryResultsPage'));
const JobsPage = lazy(() => import('./pages/JobsPage'));
const MenuServicesPage = lazy(() => import('./pages/MenuServicesPage'));
const ArticlePage1 = lazy(() => import('./pages/ArticlePage1'));
const ArticlePage2 = lazy(() => import('./pages/ArticlePage2'));
const ArticlePage3 = lazy(() => import('./pages/ArticlePage3'));
const Orders = lazy(() => import('./pages/Orders'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Shown while a route's chunk is being fetched.
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-white">
    <div className="w-10 h-10 border-[3px] border-gray-200 border-t-brand rounded-full animate-spin" />
  </div>
);

function AppContent() {
  usePageTracking();

  return (
    <>
      {/* Add ScrollToTop here */}
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Products />} />
        <Route path="/product/:pharmacode" element={<ProductDetailsPage />} />
        <Route path="/cartpage" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        <Route path="/checkout/failed" element={<CheckoutFailedPage />} />
        <Route path={ROUTES.LOGIN} element={<GuestOnlyRoute><LoginPage /></GuestOnlyRoute>} />
        <Route path={ROUTES.REGISTER} element={<GuestOnlyRoute><RegisterPage /></GuestOnlyRoute>} />
        <Route path="/orderdetailspage" element={<OrderDetailsPage />} />
        <Route path="/blogs" element={<BlogPage />} />
        <Route path="/userfavoritespage" element={<ProtectedRoute><UserFavoritesPage /></ProtectedRoute>} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
        <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmailPage />} />
        <Route path="/payment" element={<PaymentPage />} />
        <Route path="/privacypolicy" element={<PrivacyPolicyPage />} />
        <Route path="/termsconditions" element={<TermsConditionsPage />} />
        <Route path="/faqs" element={<FAQPage />} />
        <Route path="/aboutus" element={<AboutUsPage />} />
        <Route path="/categories" element={<CategoryList />} />
        <Route
          path="/categories/:subcategoryId/products"
          element={<CategoryResultsPage />}
        />
        <Route path="/jobs" element={<JobsPage />} />
        <Route path="/services" element={<MenuServicesPage />} />
        <Route path="/fasten-konvertiert" element={<ArticlePage1 />} />
        <Route path="/enigma-schlaf" element={<ArticlePage2 />} />
        <Route path="/Ferien-konvertiert" element={<ArticlePage3 />} />

        <Route
          path={ROUTES.PROFILE}
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <Orders />
            </ProtectedRoute>
          }
        />
        {/* Catch-all: unknown URLs render a proper 404 instead of a blank page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>

      {/* Mounted once, outside <Routes>, so the help chat is reachable from
          every page instead of only the two that used to render InfoModal —
          and so its open conversation survives navigation between them. It
          hides itself on checkout and payment; see HelpChat.js. */}
      <HelpChat />
    </>
  );
}

function App() {
  // Disable pinch-zoom site-wide. The viewport meta tag (user-scalable=no)
  // covers Android, but iOS Safari ignores it, so we also block the iOS
  // gesture events and any multi-touch pinch. Double-tap zoom is handled by
  // `touch-action: manipulation` in index.css.
  useEffect(() => {
    const preventDefault = (e) => e.preventDefault();
    const preventMultiTouch = (e) => {
      if (e.touches && e.touches.length > 1) e.preventDefault();
    };
    document.addEventListener('gesturestart', preventDefault);
    document.addEventListener('gesturechange', preventDefault);
    document.addEventListener('gestureend', preventDefault);
    document.addEventListener('touchmove', preventMultiTouch, { passive: false });
    return () => {
      document.removeEventListener('gesturestart', preventDefault);
      document.removeEventListener('gesturechange', preventDefault);
      document.removeEventListener('gestureend', preventDefault);
      document.removeEventListener('touchmove', preventMultiTouch);
    };
  }, []);

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </BrowserRouter>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
